import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  AclModuleCreateRequest,
  AclModuleDeleteRequest,
  AclModuleDetail,
  AclModuleRow,
  AclModuleUpdateRequest,
  AclSettingDeleteRequest,
  AclSettingDetail,
  AclSettingMutationRequest,
  AclSettingRow,
} from './types';

const FIVE_MIN = 5 * 60 * 1000;

export const aclKeys = {
  all: ['acl'] as const,
  modules: () => ['acl', 'modules'] as const,
  module: (mid: number | string | null) => ['acl', 'module', mid] as const,
  settings: (userId: number | string | null) =>
    ['acl', 'settings', userId] as const,
  setting: (id: number | string | null) => ['acl', 'setting', id] as const,
};

function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: aclKeys.all });
}

/* ============================================================
 *  ACL Modules
 * ============================================================ */

/** GET /api/obd/acl/module/list */
export function useAclModuleList() {
  return useQuery({
    queryKey: aclKeys.modules(),
    queryFn: () => api.get<AclModuleRow[]>('/api/obd/acl/module/list'),
    staleTime: FIVE_MIN,
    select: (d) => ensureArray<AclModuleRow>(d),
  });
}

/** GET /api/obd/get/acl/module/:id */
export function useAclModuleDetail(mid: number | string | null) {
  return useQuery({
    queryKey: aclKeys.module(mid),
    queryFn: () => api.get<AclModuleDetail>(`/api/obd/get/acl/module/${mid}`),
    enabled: Boolean(mid),
    staleTime: FIVE_MIN,
  });
}

/** POST /api/obd/add/acl/modules */
export function useCreateAclModule() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: AclModuleCreateRequest) =>
      api.post<{ message?: string }>('/api/obd/add/acl/modules', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/update/acl/modules */
export function useUpdateAclModule() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: AclModuleUpdateRequest) =>
      api.post<{ message?: string }>('/api/obd/update/acl/modules', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/delete/acl/modules — soft-delete (status = 0). */
export function useDeleteAclModule() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (mid: number) => {
      const body: AclModuleDeleteRequest = { mid, status: 0 };
      return api.post<{ message?: string }>('/api/obd/delete/acl/modules', body);
    },
    onSuccess: () => invalidate(),
  });
}

/* ============================================================
 *  ACL Settings
 * ============================================================ */

/** GET /api/obd/acl/setting/list/:userId */
export function useAclSettingList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: aclKeys.settings(userId),
    queryFn: () =>
      api.get<AclSettingRow[]>(`/api/obd/acl/setting/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: FIVE_MIN,
    select: (d) => ensureArray<AclSettingRow>(d),
  });
}

/** GET /api/obd/get/acl/setting/:id */
export function useAclSettingDetail(id: number | string | null) {
  return useQuery({
    queryKey: aclKeys.setting(id),
    queryFn: () => api.get<AclSettingDetail>(`/api/obd/get/acl/setting/${id}`),
    enabled: Boolean(id),
    staleTime: FIVE_MIN,
  });
}

/** POST /api/obd/add/acl/settings */
export function useCreateAclSetting() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: AclSettingMutationRequest) =>
      api.post<{ message?: string }>('/api/obd/add/acl/settings', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/update/acl/settings */
export function useUpdateAclSetting() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (body: AclSettingMutationRequest) =>
      api.post<{ message?: string }>('/api/obd/update/acl/settings', body),
    onSuccess: () => invalidate(),
  });
}

/** POST /api/obd/delete/acl/settings — soft-delete (status = 0). */
export function useDeleteAclSetting() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: number) => {
      const body: AclSettingDeleteRequest = { id, status: 0 };
      return api.post<{ message?: string }>('/api/obd/delete/acl/settings', body);
    },
    onSuccess: () => invalidate(),
  });
}
